import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, cleanObject, CustomFormatter, CustomValidators, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';

import { IPostZeitArtResponse, IZeitArt } from 'fa-kt.types';
import { ZeitArtsService } from '../../../query';
import { PostZeitArtCommandService } from '../service';

interface IFormValue {
  zeitArt?: IZeitArt;
}

@Component({
  selector: 'fa-kt-post-zeit-art',
  templateUrl: './post-zeit-art.component.html',
  styleUrls: ['./post-zeit-art.component.css'],
})
export class PostZeitArtCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostZeitArtResponse> = new EventEmitter<IPostZeitArtResponse>();

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public zeitArts: ZeitArtsService,
    public postZeitArt: PostZeitArtCommandService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.ZEITEN.Commands.POST_ZEIT_ART;
    this.model = sitemap.ZEITEN.Models.ZEIT_ART;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      zeitArt: this.fb.group({
        id: [null, []],
        name: [null, [Validators.required]],
        beschreibung: [null, []],
      }),
    });
    this.name?.setValidators([Validators.required, CustomValidators.isUnique(this.zeitArts.nameIsUnique(this.id))]);
    this.patch();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.beschreibung?.valueChanges.subscribe(async (beschreibung) => {
        /* << on-beschreibung-change    */
        /*    on-beschreibung-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let zeitArt = {
      id: null,
      name: null,
      beschreibung: null,
    };
    if (value && value.zeitArt) {
      zeitArt = {
        ...zeitArt,
        ...cleanObject(value.zeitArt),
      };
    }
    this.form.patchValue({ zeitArt });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.zeitArt) {
        payload.zeitArt = cleanObject(payload.zeitArt);
      }
      const response: IPostZeitArtResponse | undefined = await this.postZeitArt.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.zeitArt.get('id');
  }

  get name() {
    return this.form.controls.zeitArt.get('name');
  }

  get beschreibung() {
    return this.form.controls.zeitArt.get('beschreibung');
  }
}
